import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { bot } from '../../api/endpoints';
import { ApiError } from '../../api/client';
import type { LobbySummary } from '../../api/types';
import { queryKeys } from '../../queries';

/** How long the API holds one watch request open. */
export const WATCH_TIMEOUT_SECONDS = 25;
/** Backoff after a failed watch, so a broken API is not hammered. */
const RETRY_DELAY_MS = 3_000;

export type LiveState = {
  /** The watch is connected: movements land within a fraction of a second. */
  live: boolean;
  /** The API has no watch endpoint; the caller should keep polling. */
  unsupported: boolean;
};

/**
 * Follows one bot's lobby through the API's long poll and writes each answer
 * straight into the lobby-summary cache, so the seating board redraws as soon
 * as the Game Coordinator reports a movement instead of on the next poll.
 *
 * The request is held open by the server for WATCH_TIMEOUT_SECONDS and
 * re-issued immediately, so an idle lobby costs about one request per 25 s.
 * An API without the endpoint is detected once and left alone.
 */
export function useLobbyLive(
  botId: string | undefined,
  lobbyId: string | number | undefined,
  enabled: boolean,
): LiveState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<LiveState>({ live: false, unsupported: false });
  // Read inside the loop rather than captured, so a re-render cannot restart it.
  const versionRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!enabled || !botId || lobbyId === undefined) {
      setState((current) => (current.live ? { ...current, live: false } : current));
      return;
    }

    const controller = new AbortController();
    let stopped = false;
    let retryTimer: number | undefined;

    const loop = async () => {
      while (!stopped) {
        try {
          const answer = await bot.watchLobby(botId, versionRef.current, WATCH_TIMEOUT_SECONDS, controller.signal);
          if (stopped) return;
          versionRef.current = answer.version;
          if (answer.summary) {
            queryClient.setQueryData<LobbySummary>(queryKeys.lobbySummary(lobbyId), answer.summary);
          }
          setState((current) => (current.live ? current : { live: true, unsupported: false }));
        } catch (error) {
          if (stopped || controller.signal.aborted) return;
          // 404 means this API predates the watch endpoint: stop for good and
          // let the caller fall back to polling.
          if (error instanceof ApiError && error.status === 404) {
            setState({ live: false, unsupported: true });
            return;
          }
          setState((current) => (current.live ? { ...current, live: false } : current));
          versionRef.current = undefined;
          await new Promise<void>((resolve) => {
            retryTimer = window.setTimeout(resolve, RETRY_DELAY_MS);
          });
        }
      }
    };

    void loop();

    return () => {
      stopped = true;
      controller.abort();
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
  }, [botId, lobbyId, enabled, queryClient]);

  return state;
}
